import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Loader2, Plus, Copy, Trash2, ToggleLeft, ToggleRight, Ticket, Check } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AccessCodesSection = () => {
  const [codes, setCodes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [maxUses, setMaxUses] = useState(1);
  const [label, setLabel] = useState('');
  const [error, setError] = useState('');
  const [copiedCode, setCopiedCode] = useState('');
  const [confirmDelete, setConfirmDelete] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API_URL}/api/admin/access-codes`, { withCredentials: true });
      setCodes(res.data.codes || []);
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to load codes');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const createCode = async () => {
    setError('');
    setCreating(true);
    try {
      await axios.post(`${API_URL}/api/admin/access-codes`, {
        max_uses: parseInt(maxUses) || 1,
        label: label.trim(),
      }, { withCredentials: true });
      setLabel('');
      setMaxUses(1);
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreating(false);
    }
  };

  const toggleCode = async (code) => {
    try {
      await axios.patch(`${API_URL}/api/admin/access-codes/${code}/toggle`, {}, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to toggle code');
    }
  };

  const deleteCode = async (code) => {
    setConfirmDelete('');
    try {
      await axios.delete(`${API_URL}/api/admin/access-codes/${code}`, { withCredentials: true });
      await load();
    } catch (e) {
      setError(e.response?.data?.detail || 'Failed to delete code');
    }
  };

  const copy = async (code) => {
    let ok = false;
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(code);
        ok = true;
      }
    } catch {
      ok = false;
    }
    if (!ok) {
      // Fallback for iframes / non-secure contexts where Clipboard API is blocked
      try {
        const ta = document.createElement('textarea');
        ta.value = code;
        ta.setAttribute('readonly', '');
        ta.style.position = 'absolute';
        ta.style.left = '-9999px';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        ok = true;
      } catch {
        ok = false;
      }
    }
    if (ok) {
      setCopiedCode(code);
      setTimeout(() => setCopiedCode(''), 1500);
    } else {
      setError('Could not copy automatically. Long-press or select the code to copy it.');
      setTimeout(() => setError(''), 4000);
    }
  };

  return (
    <div className="card-olympiad p-6" data-testid="access-codes-section">
      <h2 className="text-base font-bold text-[#0f172a] mb-2 flex items-center gap-2" style={{ fontFamily: 'Fredoka, sans-serif' }}>
        <Ticket className="w-5 h-5 text-[#2565eb]" /> Free Registration Codes
      </h2>
      <p className="text-sm text-[#64748b] mb-5">
        Generate codes that let selected parents register their child for free, bypassing Stripe checkout. Useful for school partnerships, sponsorships, or invited families.
      </p>

      {/* Create form */}
      <div className="bg-[#f8fafc] border border-[#e2e8f0] rounded-2xl p-4 mb-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs font-semibold text-[#475569] mb-1.5">Max uses</label>
            <input
              type="number"
              min="1"
              value={maxUses}
              onChange={(e) => setMaxUses(e.target.value)}
              className="input-olympiad text-sm"
              data-testid="ac-max-uses"
            />
          </div>
          <div className="sm:col-span-2">
            <label className="block text-xs font-semibold text-[#475569] mb-1.5">Label (optional)</label>
            <input
              type="text"
              value={label}
              onChange={(e) => setLabel(e.target.value)}
              placeholder="e.g. Greenfield Academy partnership"
              className="input-olympiad text-sm"
              data-testid="ac-label"
            />
          </div>
        </div>
        <button
          onClick={createCode}
          disabled={creating}
          className="mt-4 px-5 py-2.5 rounded-xl font-bold bg-[#2565eb] text-white hover:bg-[#1e40af] transition-all text-sm flex items-center gap-2 disabled:opacity-50"
          data-testid="ac-create-btn"
        >
          {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
          Generate New Code
        </button>
      </div>

      {error && <div className="mb-4 p-3 bg-[#fef2f2] border border-[#fecaca] rounded-lg text-sm text-[#dc2626]" data-testid="ac-error">{error}</div>}

      {/* Codes list */}
      {loading ? (
        <div className="flex justify-center py-10"><Loader2 className="w-6 h-6 animate-spin text-[#2565eb]" /></div>
      ) : codes.length === 0 ? (
        <p className="text-center py-8 text-sm text-[#94a3b8]">No codes yet. Generate one above.</p>
      ) : (
        <div className="space-y-3">
          {codes.map(c => {
            const remaining = c.max_uses - c.times_used;
            const exhausted = remaining <= 0;
            return (
              <div key={c.code} className={`border rounded-2xl p-4 flex flex-col sm:flex-row sm:items-center gap-3 ${c.is_active && !exhausted ? 'border-[#e2e8f0] bg-white' : 'border-[#e2e8f0] bg-[#f8fafc] opacity-75'}`} data-testid={`ac-row-${c.code}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 flex-wrap">
                    <span className="font-mono text-base font-bold text-[#0f172a]" data-testid={`ac-code-${c.code}`}>{c.code}</span>
                    <button onClick={() => copy(c.code)} className="text-[#64748b] hover:text-[#2565eb] transition-colors" title="Copy" data-testid={`ac-copy-${c.code}`}>
                      {copiedCode === c.code ? <Check className="w-4 h-4 text-[#22c55e]" /> : <Copy className="w-4 h-4" />}
                    </button>
                    {!c.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-[#fee2e2] text-[#dc2626] font-semibold">Disabled</span>}
                    {exhausted && c.is_active && <span className="text-xs px-2 py-0.5 rounded-full bg-[#fef3c7] text-[#92400e] font-semibold">Exhausted</span>}
                  </div>
                  {c.label && <p className="text-sm text-[#64748b] mt-1">{c.label}</p>}
                  <p className="text-xs text-[#94a3b8] mt-1">
                    {c.times_used} / {c.max_uses} used · {remaining} remaining
                  </p>
                </div>
                <div className="flex items-center gap-2 self-end sm:self-auto">
                  <button
                    onClick={() => toggleCode(c.code)}
                    className="p-2 rounded-lg hover:bg-[#f1f5f9] transition-colors"
                    title={c.is_active ? 'Disable' : 'Enable'}
                    data-testid={`ac-toggle-${c.code}`}
                  >
                    {c.is_active ? <ToggleRight className="w-6 h-6 text-[#22c55e]" /> : <ToggleLeft className="w-6 h-6 text-[#64748b]" />}
                  </button>
                  {confirmDelete === c.code ? (
                    <>
                      <button onClick={() => deleteCode(c.code)} className="px-3 py-1.5 rounded-lg bg-[#ef4444] text-white text-xs font-bold hover:bg-[#dc2626]" data-testid={`ac-confirm-delete-${c.code}`}>Delete</button>
                      <button onClick={() => setConfirmDelete('')} className="px-3 py-1.5 rounded-lg border border-[#e2e8f0] text-[#64748b] text-xs font-semibold hover:bg-[#f1f5f9]">Cancel</button>
                    </>
                  ) : (
                    <button onClick={() => setConfirmDelete(c.code)} className="p-2 rounded-lg hover:bg-[#fef2f2] text-[#ef4444] transition-colors" title="Delete" data-testid={`ac-delete-${c.code}`}>
                      <Trash2 className="w-5 h-5" />
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default AccessCodesSection;
